import { Injectable, signal } from '@angular/core';

type TargetAreaIndex = Record<number, Record<number, boolean>>;

@Injectable({
  providedIn: 'root'
})
export class UiStateService {
  private readonly storageKey = 'ui.generatedTargetAreas';
  private readonly _serviceId = signal<number | null>(null);
  private readonly _locationId = signal<number | null>(null);
  private readonly _audienceId = signal<number | null>(null);
  private readonly _generatedTargetAreas = signal<TargetAreaIndex>({});

  constructor() {
    this.restoreGeneratedTargetAreas();
  }

  serviceId(): number | null {
    return this._serviceId();
  }

  locationId(): number | null {
    return this._locationId();
  }

  audienceId(): number | null {
    return this._audienceId();
  }

  setServiceId(id: number | null): void {
    this._serviceId.set(id);
  }

  setLocationId(id: number | null): void {
    this._locationId.set(id);
  }

  setAudienceId(id: number | null): void {
    this._audienceId.set(id);
  }

  isTargetAreaGenerated(
    serviceId: number | null,
    audienceId: number | null
  ): boolean {
    if (!serviceId || !audienceId) {
      return false;
    }
    const record = this._generatedTargetAreas();
    return !!record[serviceId]?.[audienceId];
  }

  markTargetAreaGenerated(serviceId: number, audienceId: number): void {
    if (!serviceId || !audienceId) {
      return;
    }

    this._generatedTargetAreas.update((current) => {
      const next: TargetAreaIndex = { ...current };
      const serviceEntry: Record<number, boolean> = {
        ...(next[serviceId] ?? {})
      };
      serviceEntry[audienceId] = true;
      next[serviceId] = serviceEntry;
      this.persistGeneratedTargetAreas(next);
      return next;
    });
  }

  unmarkTargetAreaGenerated(
    serviceId: number | null,
    audienceId: number | null
  ): void {
    if (!serviceId || !audienceId) {
      return;
    }
    this._generatedTargetAreas.update((current) => {
      if (!current[serviceId]?.[audienceId]) {
        return current;
      }
      const next: TargetAreaIndex = { ...current };
      const serviceEntry = { ...(next[serviceId] ?? {}) };
      delete serviceEntry[audienceId];

      if (Object.keys(serviceEntry).length) {
        next[serviceId] = serviceEntry;
      } else {
        delete next[serviceId];
      }
      this.persistGeneratedTargetAreas(next);
      return next;
    });
  }

  resetTargetAreaGeneration(serviceId?: number | null): void {
    if (!serviceId) {
      this._generatedTargetAreas.set({});
      this.persistGeneratedTargetAreas({});
      return;
    }

    this._generatedTargetAreas.update((current) => {
      if (!(serviceId in current)) {
        return current;
      }
      const next: TargetAreaIndex = { ...current };
      delete next[serviceId];
      this.persistGeneratedTargetAreas(next);
      return next;
    });
  }

  reset(): void {
    this._serviceId.set(null);
    this._locationId.set(null);
    this._audienceId.set(null);
    this._generatedTargetAreas.set({});
    this.persistGeneratedTargetAreas({});
  }

  private restoreGeneratedTargetAreas(): void {
    if (!this.isStorageAvailable()) {
      return;
    }
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw) as Record<
        string,
        Record<string, boolean>
      >;
      const sanitized: TargetAreaIndex = {};
      Object.entries(parsed || {}).forEach(([serviceKey, audienceMap]) => {
        const serviceId = Number(serviceKey);
        if (!Number.isFinite(serviceId) || serviceId <= 0) {
          return;
        }
        const entries: Record<number, boolean> = {};
        Object.entries(audienceMap || {}).forEach(
          ([audienceKey, flag]) => {
            const audienceId = Number(audienceKey);
            if (!Number.isFinite(audienceId) || audienceId <= 0) {
              return;
            }
            entries[audienceId] = Boolean(flag);
          }
        );
        if (Object.keys(entries).length) {
          sanitized[serviceId] = entries;
        }
      });
      this._generatedTargetAreas.set(sanitized);
    } catch {
      // Ignore malformed storage payloads.
    }
  }

  private persistGeneratedTargetAreas(index: TargetAreaIndex): void {
    if (!this.isStorageAvailable()) {
      return;
    }
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(index));
    } catch {
      // Silently ignore storage quota errors.
    }
  }

  private isStorageAvailable(): boolean {
    try {
      return typeof localStorage !== 'undefined';
    } catch {
      return false;
    }
  }
}
